/**
 * Voyado Elevate: native card renderer.
 *
 * Resolves Elevate product results into real Shopify products and renders
 * them with the theme's own snippets/product-card.liquid, via a permanent
 * Section Rendering API endpoint (sections/elevate-native-cards.liquid).
 * This is what makes Elevate-driven grids compatible with the SAMI Product
 * Labels app and with native member pricing - confirmed empirically neither
 * works against elevate-card.js's own .elevate-card markup, both work
 * against this.
 *
 * One fetch per product handle, from /products/<handle>?section_id=... - not
 * one combined fetch with a handle list in the query string. Liquid's
 * request.params/request.query_string render empty even when the real
 * request URL contains them (confirmed empirically), so there is no
 * supported way to hand the section an arbitrary list that way; the product
 * route is the one context Shopify guarantees a `product` object in.
 *
 * render() fetches a whole (small, bounded) list up front, for
 * recommendation carousels. upgradeLazily() instead fetches one card at a
 * time as it scrolls into view, for a PLP/search grid that can hold far
 * more products than is safe to fetch all at once.
 *
 * Exposed as `window.CGElevateNativeCards`. Requires elevate-core.js.
 */

(function () {
  'use strict';

  const E = window.CGElevate;
  const SECTION_ID = 'elevate-native-cards';

  /**
   * Elevate does not return a bare Shopify handle field - `product.link` is
   * the only field that carries it, as a path like `/products/<handle>`
   * (see elevate-core.js's safeUrl comment). Works on both the relative form
   * and a full absolute URL.
   */
  function handleFromLink(link) {
    const match = /\/products\/([^/?#]+)/.exec(String(link || ''));
    return match ? match[1] : '';
  }

  /**
   * One entry per product group, in Elevate's own order - order matters,
   * it's the ranking/relevance this whole integration exists for.
   */
  function extractEntries(groups) {
    return (groups || [])
      .map((group) => {
        const product = (group.products && group.products[0]) || group;
        return {
          handle: handleFromLink(product.link),
          ticket: group.ticket || product.ticket || ''
        };
      })
      .filter((entry) => entry.handle);
  }

  /**
   * `innerHTML`/`insertAdjacentHTML`/moving parsed nodes never execute
   * embedded <script> tags - confirmed empirically this session. Without
   * this, SAMI's data-push script (embedded per-card by
   * samita-product-collection.liquid) silently never runs, and the badges
   * this whole file exists for never appear.
   */
  function executeScripts(root) {
    root.querySelectorAll('script').forEach((old) => {
      const fresh = document.createElement('script');
      Array.prototype.forEach.call(old.attributes, (attr) => fresh.setAttribute(attr.name, attr.value));
      fresh.textContent = old.textContent;
      old.replaceWith(fresh);
    });
  }

  /**
   * Elevate's own click-tracking ticket has no equivalent on a native card.
   * product-card.liquid already carries `data-product="{{ handle }}"` on
   * `.card-wrapper` - matched back to the ticket captured from Elevate's
   * response, then handled exactly like elevate-card.js's own bindClicks()
   * did: a delegated listener on [data-voyado-ticket]. Without this, clicks
   * on native cards would stop reaching Elevate's own relevance tracking.
   */
  function attachTickets(container, entries) {
    const byHandle = {};
    entries.forEach((entry) => {
      if (entry.ticket) byHandle[entry.handle] = entry.ticket;
    });

    container.querySelectorAll('[data-product]').forEach((card) => {
      const ticket = byHandle[card.dataset.product];
      if (ticket) card.setAttribute('data-voyado-ticket', ticket);
    });
  }

  function bindClicks(container) {
    if (container.dataset.elevateClicksBound) return;
    container.dataset.elevateClicksBound = 'true';
    container.addEventListener('click', (event) => {
      const link = event.target.closest('[data-voyado-ticket]');
      if (!link) return;
      E.notifyClick(link.dataset.voyadoTicket);
    });
  }

  const CONCURRENCY = 6;

  /**
   * Cached for the life of the page, with no eviction and no invalidation, and
   * deliberately so: the same handle shows up across result sets as a search
   * term is refined, in more than one surface on the same page, and again when
   * a visitor repeats a search. Shopify serves this route
   * `cache-control: no-cache, no-store`, so without this every one of those is
   * a fresh round trip. A price or stock change between two renders within one
   * page session is not worth an invalidation scheme.
   */
  const cardHtmlCache = new Map();

  /**
   * One product's card as markup, or null on any failure (missing product,
   * network error, non-2xx) - a single bad handle must not take down the rest
   * of the list.
   *
   * The promise is what goes in the cache, not the resolved value, so several
   * placeholders asking for the same handle at once collapse into one request
   * rather than all missing together. Failures are dropped from the cache so a
   * transient one can be retried.
   */
  function fetchCardHtml(handle) {
    const cached = cardHtmlCache.get(handle);
    if (cached) return cached;

    const url = '/products/' + encodeURIComponent(handle) + '?section_id=' + SECTION_ID;
    const promise = fetch(url)
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const rendered = doc.getElementById('shopify-section-' + SECTION_ID);
        const card = rendered ? rendered.querySelector('.collection-product-card') : null;
        return card ? card.outerHTML : null;
      })
      .catch(() => null)
      .then((result) => {
        if (!result) cardHtmlCache.delete(handle);
        return result;
      });

    cardHtmlCache.set(handle, promise);
    return promise;
  }

  /**
   * A fresh element per call. The cache holds markup rather than a node because
   * handing one element to two placeholders lets the first insertion steal it
   * from the second. Parsed in a <template>, which accepts the card's root <li>
   * outside a list.
   */
  async function fetchCard(handle) {
    const html = await fetchCardHtml(handle);
    if (!html) return null;
    const template = document.createElement('template');
    template.innerHTML = html;
    return template.content.firstElementChild;
  }

  /**
   * Fetches every handle with at most CONCURRENCY requests in flight at
   * once, but returns results in the original, Elevate-ranked order -
   * arrival order is not ranking order.
   */
  async function fetchAll(handles) {
    const results = new Array(handles.length).fill(null);
    let next = 0;

    async function worker() {
      while (next < handles.length) {
        const i = next++;
        results[i] = await fetchCard(handles[i]);
      }
    }

    const workers = [];
    for (let i = 0; i < Math.min(CONCURRENCY, handles.length); i += 1) workers.push(worker());
    await Promise.all(workers);
    return results;
  }

  /**
   * Ask the Wishlist Guru app to draw its heart on the native cards we just
   * inserted.
   *
   * The app scans for `.th_wl_col_btn` placeholders once, early, and does not
   * reliably notice cards added afterwards. Every Elevate surface adds its
   * cards afterwards: after an async Elevate query, and again on every
   * filter, sort, page change and lazy swap. So the hearts were missing, or
   * present only when the app's scan happened to land after the cards did.
   *
   * Two races to cover, in either order:
   * - Cards land before the app's script has loaded: `injectWishlistButtonForcely`
   *   is not defined yet. Wait for it (bounded), then call it once.
   * - Cards land after the app has done its own scan: call it again.
   *
   * Debounced, because a lazily upgraded grid swaps cards one at a time and
   * one call per batch is enough. Called from here rather than from each
   * surface so the grid, search, carousels, cart recommendations and the
   * autocomplete panel all get the same behaviour.
   *
   * A no-op when the app is not installed (staging): the wait just times out.
   */
  const WISHLIST_DEBOUNCE_MS = 100;
  const WISHLIST_WAIT_POLL_MS = 250;
  const WISHLIST_WAIT_TIMEOUT_MS = 15000;

  let wishlistTimer = null;
  let wishlistWaiting = false;

  function callWishlistInject() {
    try {
      window.injectWishlistButtonForcely();
    } catch (e) {
      // A third-party app failing must never take our cards down with it.
    }
  }

  function waitForWishlistApp() {
    if (wishlistWaiting) return;
    wishlistWaiting = true;
    const deadline = Date.now() + WISHLIST_WAIT_TIMEOUT_MS;
    const check = () => {
      if (typeof window.injectWishlistButtonForcely === 'function') {
        wishlistWaiting = false;
        callWishlistInject();
        return;
      }
      if (Date.now() < deadline) {
        setTimeout(check, WISHLIST_WAIT_POLL_MS);
      } else {
        wishlistWaiting = false;
      }
    };
    check();
  }

  function requestWishlistHearts() {
    clearTimeout(wishlistTimer);
    wishlistTimer = setTimeout(() => {
      if (typeof window.injectWishlistButtonForcely === 'function') {
        callWishlistInject();
      } else {
        waitForWishlistApp();
      }
    }, WISHLIST_DEBOUNCE_MS);
  }

  /**
   * Fetches and appends native cards for `groups` into `container`, in
   * Elevate's order. Always appends - assets/elevate-recommendations.js
   * (currently the only caller) clears `container` itself first.
   */
  async function render(groups, container) {
    const entries = extractEntries(groups);
    if (!entries.length || !container) return;

    const cards = await fetchAll(entries.map((entry) => entry.handle));

    const fragment = document.createDocumentFragment();
    cards.forEach((card) => {
      if (card) fragment.appendChild(card);
    });
    if (!fragment.childNodes.length) return;
    container.appendChild(fragment);

    executeScripts(container);
    attachTickets(container, entries);
    bindClicks(container);
    requestWishlistHearts();
  }

  /**
   * PLP/search grids can hold 60+ products per page, far more than a
   * recommendation list - fetching a native card for every one of them up
   * front (like render() above does) would mean several seconds of
   * requests before a page is usable. upgradeLazily() instead leaves
   * assets/elevate-card.js's fast .elevate-card placeholders in place, and
   * swaps each one for its native card only once it actually scrolls near
   * the viewport - the same cost as render(), spread out over scrolling
   * instead of paid all at once.
   *
   * Reads the handle/ticket straight off the placeholder's own link
   * (href + data-voyado-ticket, both already set by elevate-card.js), not
   * from an Elevate `groups` array - callers just point this at a
   * container that already has placeholders in it.
   */
  const LAZY_ROOT_MARGIN = '600px 0px';
  // Matches CONCURRENCY above: the grid now shows a skeleton rather than
  // elevate-card.js's own real-data placeholder, so the visible cards above
  // the fold have no content at all until this fetch resolves - worth the
  // extra in-flight requests to keep that window short.
  const LAZY_CONCURRENCY = 6;

  const DEFAULT_PLACEHOLDER_SELECTOR = '.elevate-card';
  const DEFAULT_LINK_SELECTOR = '.elevate-card__link';

  let lazyObserver = null;
  const lazyQueue = [];
  let lazyActive = 0;

  function getLazyObserver() {
    if (lazyObserver) return lazyObserver;
    lazyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          lazyObserver.unobserve(entry.target);
          lazyQueue.push(entry.target);
          pumpLazyQueue();
        });
      },
      { rootMargin: LAZY_ROOT_MARGIN }
    );
    return lazyObserver;
  }

  function pumpLazyQueue() {
    while (lazyActive < LAZY_CONCURRENCY && lazyQueue.length) {
      const placeholder = lazyQueue.shift();
      // A queued placeholder can be detached before its turn comes up - the
      // autocomplete panel replaces its whole content on every render, so a
      // result set the visitor typed past leaves its cards queued here.
      // Fetching a card for one is a wasted round trip; measured at 12 of 39
      // fetches while typing a five-letter term.
      if (!placeholder.isConnected) continue;
      lazyActive += 1;
      upgradePlaceholder(placeholder).finally(() => {
        lazyActive -= 1;
        pumpLazyQueue();
      });
    }
  }

  async function upgradePlaceholder(placeholder) {
    // Checked again here, and before the fetch rather than after it: a
    // placeholder can be detached while it waits behind LAZY_CONCURRENCY.
    if (!placeholder.isConnected) return;
    const link = placeholder.querySelector(placeholder.dataset.elevateLinkSelector || DEFAULT_LINK_SELECTOR);
    const handle = link ? handleFromLink(link.getAttribute('href')) : '';
    if (!handle) return;

    const card = await fetchCard(handle);
    if (!card || !placeholder.isConnected) return;

    const ticket = link.dataset.voyadoTicket;
    if (ticket) card.setAttribute('data-voyado-ticket', ticket);

    placeholder.replaceWith(card);
    executeScripts(card);
    requestWishlistHearts();
  }

  /**
   * `options` lets a surface whose placeholders are not elevate-card.js's own
   * opt in without borrowing its class names: the autocomplete panel has its
   * own compact markup, and giving it `.elevate-card` would pull in
   * component-elevate-grid.css's grid styling on any page that happens to
   * load it. Defaults keep the grid's existing behaviour untouched.
   */
  function upgradeLazily(container, options) {
    if (!container) return;
    const settings = options || {};
    const placeholderSelector = settings.placeholderSelector || DEFAULT_PLACEHOLDER_SELECTOR;
    const linkSelector = settings.linkSelector || DEFAULT_LINK_SELECTOR;
    const observer = getLazyObserver();
    container.querySelectorAll(placeholderSelector + ':not([data-elevate-lazy-queued])').forEach((placeholder) => {
      placeholder.dataset.elevateLazyQueued = 'true';
      // Read back in upgradePlaceholder, which only receives the element.
      if (linkSelector !== DEFAULT_LINK_SELECTOR) placeholder.dataset.elevateLinkSelector = linkSelector;
      observer.observe(placeholder);
    });
  }

  window.CGElevateNativeCards = {
    render: render,
    upgradeLazily: upgradeLazily,
    requestWishlistHearts: requestWishlistHearts
  };
})();
